import { Fragment } from 'react';
import { PrintReport, type PrintReportDetail } from '@/components/common/PrintReport';
import { EMPTY_VALUE } from '@/constants/app.constants';
import { cn } from '@/lib/utils';
import { PRINT_CELL_CLASS, PRINT_TABLE_CLASS } from '@/styles/printTable';
import { formatDisplayDate } from '@/utils/date';
import { formatNumber } from '@/utils/number';
import type { DonationCandidate, DonationCandidateLine } from '../types/marketPromotionP2.types';

const REPORT_TITLE = 'Donation Top Sheet';
const LABEL_COLUMN_COUNT = 4;

const TEXT_CELL_CLASS = cn(PRINT_CELL_CLASS, 'text-left');
const AMOUNT_CELL_CLASS = cn(PRINT_CELL_CLASS, 'text-right tabular-nums');
const TOTAL_LABEL_CELL_CLASS = cn(PRINT_CELL_CLASS, 'text-right');

interface DonationDay {
  dcrDate: string;
  lines: DonationCandidateLine[];
  amount: number;
}

function groupByDay(lines: DonationCandidateLine[]): DonationDay[] {
  const days: DonationDay[] = [];
  for (const line of lines) {
    const lastDay = days.at(-1);
    if (lastDay?.dcrDate === line.dcrDate) {
      lastDay.lines.push(line);
      lastDay.amount += line.amount;
    } else {
      days.push({ dcrDate: line.dcrDate, lines: [line], amount: line.amount });
    }
  }
  return days;
}

const formatAmount = (amount: number): string => formatNumber(Math.round(amount));

interface DonationTopSheetPrintReportProps {
  zonalManager: DonationCandidate | null | undefined;
  lines: DonationCandidateLine[];
  dateFrom: string;
  dateTo: string;
}

export function DonationTopSheetPrintReport({
  zonalManager,
  lines,
  dateFrom,
  dateTo,
}: DonationTopSheetPrintReportProps) {
  const details: PrintReportDetail[] = [
    { label: 'Name', value: zonalManager?.fullName ?? undefined },
    { label: 'HR ID', value: zonalManager?.hrEmpId ?? undefined },
    { label: 'Mobile', value: zonalManager?.mobile ?? undefined },
    { label: 'Donation Name', value: zonalManager?.itemName ?? undefined },
    { label: 'From Date', value: formatDisplayDate(dateFrom) },
    { label: 'To Date', value: formatDisplayDate(dateTo) },
  ];
  const days = groupByDay(lines);
  const grandTotal = lines.reduce((total, line) => total + line.amount, 0);

  return (
    <PrintReport title={REPORT_TITLE} details={details}>
      <table className={PRINT_TABLE_CLASS}>
        <thead>
          <tr>
            <th className={cn(TEXT_CELL_CLASS, 'font-bold')}>SL</th>
            <th className={cn(TEXT_CELL_CLASS, 'font-bold')}>Date</th>
            <th className={cn(TEXT_CELL_CLASS, 'font-bold')}>Institute</th>
            <th className={cn(TEXT_CELL_CLASS, 'font-bold')}>Teacher</th>
            <th className={cn(AMOUNT_CELL_CLASS, 'font-bold')}>Amount</th>
          </tr>
        </thead>
        <tbody>
          {days.map((day) => (
            <Fragment key={day.dcrDate}>
              {day.lines.map((line, index) => (
                <tr key={line.detailId}>
                  <td className={TEXT_CELL_CLASS}>{index + 1}</td>
                  <td className={TEXT_CELL_CLASS}>{formatDisplayDate(line.dcrDate)}</td>
                  <td className={TEXT_CELL_CLASS}>{line.instituteName ?? EMPTY_VALUE}</td>
                  <td className={TEXT_CELL_CLASS}>{line.teacherName ?? EMPTY_VALUE}</td>
                  <td className={AMOUNT_CELL_CLASS}>{formatAmount(line.amount)}</td>
                </tr>
              ))}
              <tr className="font-semibold">
                <td colSpan={LABEL_COLUMN_COUNT} className={TOTAL_LABEL_CELL_CLASS}>
                  Total for {formatDisplayDate(day.dcrDate)}
                </td>
                <td className={AMOUNT_CELL_CLASS}>{formatAmount(day.amount)}</td>
              </tr>
            </Fragment>
          ))}
          <tr className="font-bold">
            <td colSpan={LABEL_COLUMN_COUNT} className={TOTAL_LABEL_CELL_CLASS}>
              Grand Total
            </td>
            <td className={AMOUNT_CELL_CLASS}>{formatAmount(grandTotal)}</td>
          </tr>
        </tbody>
      </table>
    </PrintReport>
  );
}
